import React from 'react';
import ObservationEntryTable from './ObservationEntryTable';

export const TabWiseResultEntry = ({
  tests = [],
  activeTestId,
  onSelectTestTab,
  selectedTestIds = new Set(),
  onToggleTestCheckbox,
  testObservationsMap = {},
  instrumentResultsMap = {},
  onObservationChange,
  onRemarksChange,
  onFillDefaults,
  onOpenParameterHistory,
  loadingForm = false,
  onOpenRecordOutsourceModal,
  onVerifyOutsource,
}) => {
  const activeTest = tests.find((t) => t.testId === activeTestId) || tests[0];
  const activeTestObs = activeTest ? testObservationsMap[activeTest.testId] || [] : [];

  const currentIndex = tests.findIndex((t) => t.testId === activeTest?.testId);
  const prevTest = currentIndex > 0 ? tests[currentIndex - 1] : null;
  const nextTest = currentIndex >= 0 && currentIndex < tests.length - 1 ? tests[currentIndex + 1] : null;

  return (
    <div className="path-layout-tabwise">
      {/* Horizontal Tabs Bar */}
      <div className="path-tabs-header-bar">
        <div className="path-tabs-scroller">
          {tests.map((t) => {
            const isSelectedTab = t.testId === activeTest?.testId;
            const isChecked = selectedTestIds.has(t.testId);
            const isApproved = t.approved === 1;
            const isDraft = t.resultFlag === 1 && !isApproved;
            const isOutsourced = Boolean(t.isOutsourced);
            const isRejected = Boolean(t.isRejected || t.isSampleCollected === 'R');
            const isHold = Boolean(t.isHold || t.status === 'Hold');
            const tabTitle = t.investigationName || t.testName || `Test #${t.testId}`;

            let tabStatusClass = 'tab-pending';
            let tabStatusBadge = '○ Pending';
            if (isApproved) {
              tabStatusClass = 'tab-approved';
              tabStatusBadge = '✓ Approved';
            } else if (isHold) {
              tabStatusClass = 'tab-hold';
              tabStatusBadge = '⏸ Hold';
            } else if (isDraft) {
              tabStatusClass = 'tab-draft';
              tabStatusBadge = isOutsourced ? '● Awaiting verification' : '● Draft';
            } else if (isRejected) {
              tabStatusClass = 'tab-rejected';
              tabStatusBadge = '🚫 Rejected';
            } else if (isOutsourced) {
              tabStatusClass = 'tab-outsourced';
              tabStatusBadge = '🌐 Outsourced';
            }

            return (
              <div
                key={t.testId}
                className={`path-test-tab ${isSelectedTab ? 'active tab-active' : ''} ${tabStatusClass}`}
                onClick={() => onSelectTestTab(t.testId)}
                title={`Test #${t.testId}: ${tabTitle}${isOutsourced ? ` (Outsourced to ${t.referenceLabName || 'Partner Lab'})` : ''}${isRejected ? ' (Sample Rejected)' : ''}`}
              >
                <input
                  type="checkbox"
                  className="path-tab-checkbox"
                  checked={isChecked}
                  disabled={isOutsourced || isRejected}
                  onChange={(e) => {
                    e.stopPropagation();
                    onToggleTestCheckbox(t.testId);
                  }}
                  title={isOutsourced || isRejected ? 'Non-editable investigation' : (isChecked ? 'Uncheck test' : 'Check test for batch action')}
                />
                <span className="path-tab-title">{tabTitle}</span>
                <span className="path-tab-badge">
                  {tabStatusBadge}
                </span>
              </div>
            );
          })}
        </div>

        {/* Right side navigation & status controls */}
        <div className="path-tabs-header-right">
          <div className="path-tab-nav-buttons">
            <button
              type="button"
              className="path-btn path-btn-sm path-btn-secondary"
              disabled={!prevTest}
              onClick={() => prevTest && onSelectTestTab(prevTest.testId)}
              title={prevTest ? `Previous Test: ${prevTest.investigationName}` : 'First test reached'}
            >
              ◀ Prev Test
            </button>
            <button
              type="button"
              className="path-btn path-btn-sm path-btn-secondary"
              disabled={!nextTest}
              onClick={() => nextTest && onSelectTestTab(nextTest.testId)}
              title={nextTest ? `Next Test: ${nextTest.investigationName}` : 'Last test reached'}
            >
              Next Test ▶
            </button>
          </div>

          {activeTest && (
            <div className="path-active-tab-status">
              <span className="status-label">Status:</span>
              <span
                className={`path-status-badge ${
                  activeTest.approved === 1
                    ? 'status-approved'
                    : activeTest.resultFlag === 1
                    ? 'status-draft'
                    : activeTest.isRejected
                    ? 'status-rejected'
                    : activeTest.isOutsourced
                    ? 'status-outsourced'
                    : 'status-pending'
                }`}
              >
                {activeTest.approved === 1
                  ? 'Approved'
                  : activeTest.resultFlag === 1
                  ? (activeTest.isOutsourced ? 'Awaiting verification' : 'Draft')
                  : activeTest.isRejected
                  ? 'Sample Rejected'
                  : activeTest.isOutsourced
                  ? 'Outsourced'
                  : 'Pending Entry'}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Active Tab Panel */}
      <div className="path-tab-panel">
        {activeTest ? (
          <div className="path-active-test-card">

            {/* Outsource Informative Alert Banner */}
            {activeTest.isOutsourced && (
              <div className="path-alert-banner path-alert-banner-outsource">
                <div className="path-alert-banner-icon">🌐</div>
                <div className="path-alert-banner-body">
                  <div className="path-alert-banner-title">
                    OUTSOURCED INVESTIGATION — {activeTest.referenceLabName || 'Partner Reference Laboratory'}
                  </div>
                  <div className="path-alert-banner-desc">
                    This sample was outsourced to <strong>{activeTest.referenceLabName || 'External Reference Laboratory'}</strong>.
                    {activeTest.outsourceStatus && (
                      <span className="path-outsource-status-badge">Status: {activeTest.outsourceStatus}</span>
                    )}
                  </div>
                  {activeTest.externalResultSummary && (
                    <div className="path-outsource-summary-panel">
                      <span className="summary-title">Partner Result Summary:</span>
                      <p className="summary-text">{activeTest.externalResultSummary}</p>
                    </div>
                  )}
                  <div className="path-outsource-action-row" style={{ marginTop: '10px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="path-btn path-btn-sm"
                      onClick={() => onOpenRecordOutsourceModal && onOpenRecordOutsourceModal(activeTest)}
                      style={{ background: '#7c3aed', color: '#ffffff', borderColor: '#6d28d9', fontWeight: 600, padding: '4px 12px' }}
                    >
                      📤 {activeTest.externalResultSummary || activeTest.externalAttachmentUrl ? 'Edit / Replace Partner Report' : 'Upload / Record Partner Report'}
                    </button>
                    {onVerifyOutsource && activeTest.approved !== 1 && activeTest.outsourceStatus === 'RESULT_RECEIVED' && (
                      <button
                        type="button"
                        className="path-btn path-btn-sm"
                        onClick={() => onVerifyOutsource(activeTest)}
                        style={{ background: '#059669', color: '#ffffff', borderColor: '#047857', fontWeight: 600, padding: '4px 12px' }}
                      >
                        ✅ Verify &amp; Release Report
                      </button>
                    )}
                    {activeTest.approved === 1 && (
                      <span className="path-attachment-pill" style={{ color: '#047857', borderColor: '#a7f3d0', background: '#ecfdf5' }}>
                        ✅ Verified &amp; released
                      </span>
                    )}
                    {activeTest.externalAttachmentUrl && (
                      <a
                        href={activeTest.externalAttachmentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="path-attachment-pill"
                      >
                        📄 View Partner Report Document ↗
                      </a>
                    )}
                  </div>
                  <div className="path-freeze-notice">
                    🔒 {activeTest.approved === 1
                      ? 'This partner report has been verified and released.'
                      : activeTest.outsourceStatus === 'RESULT_RECEIVED'
                      ? 'Partner report received. Review it, then verify and release it to the patient report.'
                      : 'Result observation entry is handled via external report upload / summary.'}
                  </div>
                </div>
              </div>
            )}

            {/* Rejection Informative Alert Banner */}
            {activeTest.isRejected && (
              <div className="path-alert-banner path-alert-banner-rejected">
                <div className="path-alert-banner-icon">🚫</div>
                <div className="path-alert-banner-body">
                  <div className="path-alert-banner-title">SPECIMEN REJECTED</div>
                  <div className="path-alert-banner-desc">
                    This specimen was rejected during reception/collection.
                    {activeTest.rejectionReason && (
                      <span className="path-rejection-reason-pill">Reason: {activeTest.rejectionReason}</span>
                    )}
                  </div>
                  <div className="path-freeze-notice">
                    🔒 Result entry is frozen. A fresh specimen collection (re-draw) is required to process this investigation.
                  </div>
                </div>
              </div>
            )}

            {loadingForm ? (
              <div className="path-loading-box">
                <span>⏳ Loading test observations...</span>
              </div>
            ) : (
              <ObservationEntryTable
                testId={activeTest.testId}
                observations={activeTestObs}
                instrumentResultsMap={instrumentResultsMap}
                onObservationChange={onObservationChange}
                onRemarksChange={onRemarksChange}
                onFillDefaults={onFillDefaults}
                onOpenParameterHistory={onOpenParameterHistory}
                readOnly={activeTest.approved === 1 || activeTest.isOutsourced || activeTest.isRejected}
              />
            )}
          </div>
        ) : (
          <div className="path-empty-state">
            <p>No tests available for this patient.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TabWiseResultEntry;
